import React, { useState, useEffect, useRef, useCallback } from "react";
import { Toaster, toast } from "react-hot-toast";
import { Loader2 } from "lucide-react";
import HomePage from "./pages/HomePage";
import EditorPage from "./pages/EditorPage";
import NotFoundPage from "./pages/NotFoundPage";
import FaqPage from "./pages/FaqPage";
import SupportPage from "./pages/SupportPage";
import PrivacyPage from "./pages/PrivacyPage";
import OfferPage from "./pages/OfferPage";
import TariffsPage from "./pages/TariffsPage";
import AccountPage from "./pages/AccountPage";
import SettingsPage from "./pages/SettingsPage";
import TitlePage from "./pages/TitlePage";
import ExamplesPage from "./pages/ExamplesPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ForgotPasswordSentPage from "./pages/ForgotPasswordSentPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ResetPasswordSuccessPage from "./pages/ResetPasswordSuccessPage";
import ResetPasswordInvalidPage from "./pages/ResetPasswordInvalidPage";
import PaymentModal from "./components/PaymentModal";
import PaymentSuccessModal from "./components/PaymentSuccessModal";
import ShareModal from "./components/ShareModal";
import ShareSiteModal from "./components/ShareSiteModal";
import LoginModal from "./components/LoginModal";
import { logout } from "./auth";
import { apiFetch, setAuthErrorHandler, API_BASE_URL, pingVisitor } from "./api";
import { DEFAULT_PLAN_GENERATION_TYPE } from "./constants/planGenerationTypes";
import { setPlanAccessCode, removePlanAccessCode } from "./utils/planAccess";
import { createEmptyPlanStream, createPlanStreamParser, PLAN_STREAM_SECTION_KEYS } from "./utils/planStreamParser";
import { revealStreamTarget, STREAM_REVEAL_DELAY } from "./utils/streamReveal";
// Snapshot-based resume is used; no client-side localStorage cache for chunks

const API = "/plan";
const ACTIVE_TASKS_STORAGE_KEY = "activePlanTasks";
const AUTO_REGEN_GUARDS_STORAGE_KEY = "autoRegenGuards";
const AUTO_REGEN_GUARD_TTL_MS = 10 * 60 * 1000; // 10 minutes
const IMPORTANT_STREAM_SECTIONS = ["summary", "description"];
const CHUNK_WATCHDOG_INITIAL_DELAY = 60000;
const CHUNK_WATCHDOG_REGULAR_DELAY = 15000;
const SECTION_STALL_THRESHOLD = 60000;
const SECTION_STALL_CHECK_INTERVAL = 5000;
const VISIBILITY_IDLE_THRESHOLD = 20000;
const VISITOR_ID_STORAGE_KEY = "visitorId";
const VISITOR_PING_INTERVAL_MS = 10000;
const createImportantSectionMap = () =>
  IMPORTANT_STREAM_SECTIONS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {});
const createImportantSectionActivityMap = () =>
  IMPORTANT_STREAM_SECTIONS.reduce((acc, key) => {
    acc[key] = { lastLength: 0, lastChangeTs: 0, lastWarnTs: 0 };
    return acc;
  }, {});

const generateVisitorId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `v-${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`;
};

const readVisitorId = () => {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(VISITOR_ID_STORAGE_KEY);
    if (stored) return stored;
    const fresh = generateVisitorId();
    window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch (_) {
    return null;
  }
};

const readStoredActiveTasks = () => {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(ACTIVE_TASKS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed;
    }
  } catch (err) {
    console.warn("Не удалось прочитать активные задачи планов", err);
  }
  return {};
};

const routePaths = {
  home: "/",
  faq: "/faq",
  support: "/support",
  policy: "/policy",
  offer: "/offer",
  tariffs: "/tariffs",
  settings: "/settings",
  account: "/account",
  examples: "/examples",
  accessDenied: "/access-denied",
  forgotPassword: "/forgot-password",
  forgotPasswordSent: "/forgot-password/sent",
  resetPassword: "/reset-password",
  resetPasswordSuccess: "/reset-password/success",
  resetPasswordInvalid: "/reset-password/invalid",
};

const parseLocation = (path) => {
  const decode = (value) => {
    try {
      return decodeURIComponent(value);
    } catch (_) {
      return value;
    }
  };

  // Editor/title routes keep uuid in path explicitly
  let m = path.match(/^\/editor\/([\w-]+)$/);
  if (m) return { route: "editor", planUuid: m[1], articleName: null };
  if (path === "/editor") return { route: "editor", planUuid: null, articleName: null };

  m = path.match(/^\/title\/([\w-]+)$/);
  if (m) return { route: "title", planUuid: m[1], articleName: null };
  if (path === "/title") return { route: "title", planUuid: null, articleName: null };

  m = path.match(/^\/share\/([\w-]+)$/);
  if (m) return { route: "share", planUuid: m[1], articleName: null };

  if (path === "/access-denied") return { route: "accessDenied", planUuid: null, articleName: null };

  if (path === "/") return { route: "home", planUuid: null, articleName: null };

  const orderedRoutes = Object.entries(routePaths)
    .filter(([key]) => key !== "home")
    .sort(([, a], [, b]) => b.length - a.length);

  for (const [key, base] of orderedRoutes) {
    if (path === base || path.startsWith(`${base}/`)) {
      return { route: key, planUuid: null, articleName: null };
    }
  }

  // Fallback to notFound page
  return { route: "notFound", planUuid: null, articleName: null };
};

// Derive plan UUID from URL for editor/title routes.
// Used as a fallback source of truth when selecting which plan to focus.
const getUrlPlanUuid = () => {
  try {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const m = pathname.match(/^\/(editor|title)\/([\w-]+)$/);
    return m ? m[2] : null;
  } catch (_) {
    return null;
  }
};

const getUrlPlanVersion = () => {
  try {
    const value = new URLSearchParams(window.location.search).get("version");
    const version = Number.parseInt(value || "", 10);
    return Number.isInteger(version) && version > 0 ? version : null;
  } catch (_) {
    return null;
  }
};

export default function App() {
  const initial = parseLocation(window.location.pathname);
  const [route, setRoute] = useState(initial.route);
  const [planUuid, setPlanUuid] = useState(initial.planUuid);
  const [editorVersion, setEditorVersion] = useState(getUrlPlanVersion);
  // This ref is updated synchronously when a user selects a version.  It makes
  // responses from older, still pending requests harmless.
  const selectedPlanVersionRef = useRef({
    uuid: initial.planUuid || null,
    version: getUrlPlanVersion(),
  });
  const [pendingEditorAnchor, setPendingEditorAnchor] = useState(null);
  const [articleName, setArticleName] = useState(initial.articleName || null);
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [direction, setDirection] = useState("");
  const [generationType, setGenerationType] = useState(DEFAULT_PLAN_GENERATION_TYPE);
  const [city, setCity] = useState("");
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [openPay, setOpenPay] = useState(false);
  const [paid, setPaid] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("NOT_PAID");
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [status, setStatus] = useState("DRAFT");
  const [useOwnFunds, setUseOwnFunds] = useState(false);
  const [ownFunds, setOwnFunds] = useState("");
  const [cfgShareEnabled, setCfgShareEnabled] = useState(true);
  const [cfgBlogEnabled, setCfgBlogEnabled] = useState(true);
  const [cfgShowReviewsEnabled, setCfgShowReviewsEnabled] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [paidPlanUuid, setPaidPlanUuid] = useState(null);
  const statusInterval = useRef(null);

  const selectPlanVersion = (uuid, version) => {
    const normalizedVersion = Number.isInteger(version) && version > 0 ? version : null;
    selectedPlanVersionRef.current = {
      uuid: uuid ? String(uuid) : null,
      version: normalizedVersion,
      loading: false,
    };
    setPlanUuid(uuid || null);
    setEditorVersion(normalizedVersion);
  };

  const lockPlanContentUntilVersionIsSelected = (uuid) => {
    selectedPlanVersionRef.current = {
      uuid: uuid ? String(uuid) : null,
      version: null,
      loading: true,
    };
  };

  const isSelectedPlanVersion = (uuid, version) => {
    const selected = selectedPlanVersionRef.current;
    if (selected.loading) {
      return false;
    }
    const normalizedVersion = Number.isInteger(version) && version > 0 ? version : null;
    if (selected.uuid && String(selected.uuid) !== String(uuid)) {
      return false;
    }
    return !selected.version || !normalizedVersion || selected.version === normalizedVersion;
  };
  const generationErrorToastRef = useRef({ taskId: null, planUuid: null });
  const streamSource = useRef(null);
  const streamParserRef = useRef(createPlanStreamParser());
  const streamUpdateQueueRef = useRef(new Map());
  const streamFlushTimerRef = useRef(null);
  const [streamSections, setStreamSections] = useState(() => createEmptyPlanStream());
  const streamSectionsRef = useRef(createEmptyPlanStream());
  const [streamSectionStatus, setStreamSectionStatus] = useState({});
  const streamActiveSectionRef = useRef(null);
  const streamSectionStatusRef = useRef({});
  const sectionStatusLogRef = useRef({});
  const importantSectionLengthsRef = useRef(createImportantSectionMap());
  const activeTasksRef = useRef(null);
  const lastEventIdRef = useRef(null);
  const sectionStatusIntervalRef = useRef(null);
  // Watchdog for SSE chunk activity, выдерживает первый тайм-аут дольше
  const chunkWatchdogRef = useRef({
    timer: null,
    activeKey: null,
    awaitingFirstChunk: false,
    lastChunkAt: null,
  });
  const importantSectionActivityRef = useRef(createImportantSectionActivityMap());
  const sectionStallIntervalRef = useRef(null);
  const loadSnapshotAndStartRef = useRef(null);
  const visibilitySnapshotRef = useRef(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareSiteModal, setShowShareSiteModal] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareActivating, setShareActivating] = useState(false);
  const [shareProcessing, setShareProcessing] = useState(false);
  const [shareRouteError, setShareRouteError] = useState(null);
  // Unique per-tab id to help with debugging auto-regeneration across duplicated tabs
  const tabIdRef = useRef(null);
  if (tabIdRef.current === null) {
    try {
      const existing = window.sessionStorage.getItem('tabId');
      if (existing) {
        tabIdRef.current = existing;
      } else {
        const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
        window.sessionStorage.setItem('tabId', id);
        tabIdRef.current = id;
      }
    } catch (_) {
      tabIdRef.current = 'no-session';
    }
  }
  if (activeTasksRef.current === null) {
    activeTasksRef.current = readStoredActiveTasks();
  }

  const persistActiveTasks = (tasks) => {
    activeTasksRef.current = tasks;
    if (typeof window === "undefined") {
      return;
    }
    if (Object.keys(tasks).length > 0) {
      window.localStorage.setItem(ACTIVE_TASKS_STORAGE_KEY, JSON.stringify(tasks));
    } else {
      window.localStorage.removeItem(ACTIVE_TASKS_STORAGE_KEY);
    }
  };

  const saveActiveTaskMapping = (uuid, id) => {
    if (!uuid || !id) return;
    persistActiveTasks({ ...activeTasksRef.current, [uuid]: id });
  };

  const removeActiveTaskMapping = (uuid) => {
    if (!uuid) return;
    if (!activeTasksRef.current[uuid]) return;
    const next = { ...activeTasksRef.current };
    delete next[uuid];
    persistActiveTasks(next);
  };

  const getActiveTaskId = (uuid) => {
    if (!uuid) return null;
    return activeTasksRef.current[uuid] || null;
  };

  // --- Auto-regeneration guard helpers (cross-tab) ---
  const readAutoRegenGuards = () => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem(AUTO_REGEN_GUARDS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  };
  const writeAutoRegenGuards = (guards) => {
    if (typeof window === 'undefined') return;
    try {
      const keys = Object.keys(guards || {});
      if (keys.length === 0) {
        window.localStorage.removeItem(AUTO_REGEN_GUARDS_STORAGE_KEY);
      } else {
        window.localStorage.setItem(AUTO_REGEN_GUARDS_STORAGE_KEY, JSON.stringify(guards));
      }
    } catch (_) {}
  };
  const getAutoRegenGuard = (uuid) => {
    if (!uuid) return null;
    const guards = readAutoRegenGuards();
    return guards[uuid] || null;
  };
  const setAutoRegenGuard = (uuid, reason) => {
    if (!uuid) return;
    const guards = readAutoRegenGuards();
    guards[uuid] = { ts: Date.now(), reason: reason || 'unknown', tabId: tabIdRef.current };
    writeAutoRegenGuards(guards);
  };
  const isAutoRegenGuardActive = (uuid) => {
    const entry = getAutoRegenGuard(uuid);
    if (!entry) return false;
    return Date.now() - (entry.ts || 0) < AUTO_REGEN_GUARD_TTL_MS;
  };

  const resetStreamProgress = () => {
    streamActiveSectionRef.current = null;
    setStreamSectionStatus((prev) => {
      if (!prev || Object.keys(prev).length === 0) {
        return prev;
      }
      return {};
    });
  };

  const clearStreamQueue = () => {
    streamUpdateQueueRef.current = new Map();
    if (streamFlushTimerRef.current !== null) {
      clearTimeout(streamFlushTimerRef.current);
      streamFlushTimerRef.current = null;
    }
  };

  const finalizeActiveStreamSection = () => {
    const activeKey = streamActiveSectionRef.current;
    if (!activeKey) return;
    setStreamSectionStatus((prev) => {
      if (prev?.[activeKey] === "done") {
        return prev;
      }
      const next = { ...prev, [activeKey]: "done" };
      return next;
    });
    streamActiveSectionRef.current = null;
  };

  const completeStreamSection = (key) => {
    if (!key || !PLAN_STREAM_SECTION_KEYS.includes(key)) return;
    const target = streamUpdateQueueRef.current.get(key);
    if (typeof target === "string") {
      streamUpdateQueueRef.current.delete(key);
      const current = streamSectionsRef.current || createEmptyPlanStream();
      if (current[key] !== target) {
        const next = { ...current, [key]: target };
        streamSectionsRef.current = next;
        setStreamSections(next);
      }
    }
    setStreamSectionStatus((prev) => {
      if (prev?.[key] === "done") return prev;
      return { ...prev, [key]: "done" };
    });
    if (streamActiveSectionRef.current === key) {
      streamActiveSectionRef.current = null;
    }
  };

  const updateStreamSectionStates = (keys) => {
    if (!keys || keys.length === 0) {
      return;
    }
    setStreamSectionStatus((prev) => {
      const next = { ...prev };
      let active = streamActiveSectionRef.current;
      let changed = false;

      keys.forEach((key) => {
        if (!key || !PLAN_STREAM_SECTION_KEYS.includes(key)) return;
        if (active === key) {
          if (next[key] !== "in_progress" && next[key] !== "done") {
            next[key] = "in_progress";
            changed = true;
          }
          return;
        }
        if (active && PLAN_STREAM_SECTION_KEYS.includes(active) && next[active] !== "done") {
          next[active] = "done";
          changed = true;
        }
        if (next[key] !== "done") {
          if (next[key] !== "in_progress") {
            next[key] = "in_progress";
            changed = true;
          }
          active = key;
        } else {
          active = null;
        }
      });

      if (active && next[active] === "done") {
        active = null;
      }
      streamActiveSectionRef.current = active && PLAN_STREAM_SECTION_KEYS.includes(active) ? active : null;
      if (!changed) {
        return prev;
      }
      return next;
    });
  };

  const enqueueStreamUpdates = (updates) => {
    if (!updates || updates.length === 0) return;
    const queue = streamUpdateQueueRef.current;
    updates.forEach(({ key, value }) => {
      if (!key || !PLAN_STREAM_SECTION_KEYS.includes(key) || typeof value !== "string") return;
      const displayed = streamSectionsRef.current?.[key] || "";
      const queued = queue.get(key);
      const baseline = typeof queued === "string" ? queued : displayed;
      if (value.length < baseline.length && baseline.startsWith(value)) return;
      queue.set(key, value);
    });
  };

  const applyStreamQueueBatch = () => {
    const queue = streamUpdateQueueRef.current;
    if (queue.size === 0) {
      return { appliedKeys: [], appliedInfo: [] };
    }
    const current = streamSectionsRef.current || createEmptyPlanStream();
    let next = current;
    const appliedKeys = [];
    const appliedInfo = [];
    for (const [key, target] of queue.entries()) {
      const displayed = current[key] || "";
      if (displayed === target) {
        queue.delete(key);
        continue;
      }
      const revealed = revealStreamTarget(displayed, target);
      next = { ...current, [key]: revealed.value };
      if (revealed.done) queue.delete(key);
      appliedKeys.push(key);
      if (IMPORTANT_STREAM_SECTIONS.includes(key)) {
        appliedInfo.push({ key, len: (next[key] || "").length });
      }
      // Generation is sequential. Updating one section per frame keeps Markdown work bounded.
      break;
    }
    if (next !== current) {
      streamSectionsRef.current = next;
      setStreamSections(next);
    }
    return { appliedKeys, appliedInfo };
  };

  const scheduleStreamFlush = ({ immediate = false } = {}) => {
    if (streamUpdateQueueRef.current.size === 0) {
      return;
    }

    if (streamFlushTimerRef.current !== null) {
      if (!immediate) {
        return;
      }
      clearTimeout(streamFlushTimerRef.current);
      streamFlushTimerRef.current = null;
    }

    const scheduleNext = (delay) => {
      try {
        const logId =
          streamRetryRef.current?.taskId ||
          taskId ||
          planUuid ||
          (currentPlan ? currentPlan.uuid : null) ||
          "unknown";
        // eslint-disable-next-line no-console
        // console.log(
        //   `[Plan stream][${logId}] schedule flush: queue=${streamUpdateQueueRef.current.size} delay=${delay}`
        // );
      } catch (_) {}
      streamFlushTimerRef.current = window.setTimeout(() => {
        streamFlushTimerRef.current = null;
        const queue = streamUpdateQueueRef.current;
        if (queue.size === 0) {
          return;
        }
        const { appliedInfo } = applyStreamQueueBatch();
        if (appliedInfo.length > 0) {
          // Diagnostic: log applied important section lengths
          try {
            const logId =
              streamRetryRef.current?.taskId ||
              taskId ||
              planUuid ||
              (currentPlan ? currentPlan.uuid : null) ||
              "unknown";
            appliedInfo.forEach((info) => {
              // console.log(
              //   `[Plan stream][${logId}][${info.key}] применено обновление (${info.len} символов)`
              // );
            });
          } catch (_) {
            // ignore
          }
        }

        if (streamUpdateQueueRef.current.size > 0) {
          scheduleNext(STREAM_REVEAL_DELAY);
        }
      }, delay);
    };

    const delay = immediate ? 0 : STREAM_REVEAL_DELAY;

    scheduleNext(delay);
  };

  const forceStreamQueueFlush = (reason = "manual") => {
    if (streamUpdateQueueRef.current.size === 0) {
      return false;
    }
    const { appliedKeys } = applyStreamQueueBatch();
    if (appliedKeys.length === 0) {
      return false;
    }
    try {
      const logId =
        streamRetryRef.current?.taskId ||
        taskId ||
        planUuid ||
        (currentPlan ? currentPlan.uuid : null) ||
        "unknown";
      console.warn(
        `[Plan stream][${logId}] принудительный flush очереди (${reason}); обновлены: ${appliedKeys.join(", ")}`
      );
    } catch (_) {}
    return true;
  };

  const streamRetryRef = useRef({ attempts: 0, timer: null, taskId: null });
  const latestStatusRef = useRef(status);

  const getStreamLogId = () =>
    streamRetryRef.current?.taskId ||
    taskId ||
    planUuid ||
    (currentPlan ? currentPlan.uuid : null) ||
    "unknown";

  const resetChunkWatchdog = (reason = "unspecified") => {
    const watchdog = chunkWatchdogRef.current;
    if (watchdog.timer) {
      clearTimeout(watchdog.timer);
      watchdog.timer = null;
    }
    watchdog.activeKey = null;
    watchdog.awaitingFirstChunk = false;
    watchdog.lastChunkAt = null;
  };

  const scheduleChunkWatchdog = (delayMs, phase) => {
    const watchdog = chunkWatchdogRef.current;
    if (!watchdog.activeKey) {
      return;
    }
    if (watchdog.timer) {
      clearTimeout(watchdog.timer);
    }
    watchdog.timer = window.setTimeout(() => {
      const logId = getStreamLogId();
      const sinceLast = watchdog.lastChunkAt
        ? `${Math.round((Date.now() - watchdog.lastChunkAt) / 1000)}с назад`
        : "ещё ни одного чанка";
      console.warn(
        `[Plan stream][${logId}][watchdog] раздел ${watchdog.activeKey} не получает SSE чанки (${phase}, задержка ${Math.round(
          delayMs / 1000
        )}с, ${sinceLast})`
      );
      watchdog.awaitingFirstChunk = false;
      scheduleChunkWatchdog(CHUNK_WATCHDOG_REGULAR_DELAY, "post-timeout");
    }, delayMs);
  };

  const armChunkWatchdogForSection = (sectionKey, { source = "unknown" } = {}) => {
    const watchdog = chunkWatchdogRef.current;
    if (watchdog.timer) {
      clearTimeout(watchdog.timer);
      watchdog.timer = null;
    }
    watchdog.activeKey = sectionKey;
    watchdog.awaitingFirstChunk = Boolean(sectionKey);
    watchdog.lastChunkAt = null;
    if (!sectionKey) {
      return;
    }
    scheduleChunkWatchdog(CHUNK_WATCHDOG_INITIAL_DELAY, `${source}-initial`);
  };

  const handleChunkWatchdogActivity = (source = "chunk") => {
    const watchdog = chunkWatchdogRef.current;
    if (!watchdog.activeKey) {
      return;
    }
    watchdog.lastChunkAt = Date.now();
    watchdog.awaitingFirstChunk = false;
    scheduleChunkWatchdog(CHUNK_WATCHDOG_REGULAR_DELAY, `${source}-activity`);
  };

  const stopSectionStallInterval = () => {
    if (sectionStallIntervalRef.current) {
      clearInterval(sectionStallIntervalRef.current);
      sectionStallIntervalRef.current = null;
    }
  };

  // Отдельный страж следит, что summary/description растут и приостанавливает очередь,
  // если длины не менялись слишком долго.
  const runImportantSectionStallCheck = () => {
    if (latestStatusRef.current !== "PROCESSING") {
      return;
    }
    const now = Date.now();
    IMPORTANT_STREAM_SECTIONS.forEach((key) => {
      const activity = importantSectionActivityRef.current[key];
      if (!activity) return;
      const lastChange = activity.lastChangeTs || 0;
      if (lastChange === 0) return;
      if (now - lastChange < SECTION_STALL_THRESHOLD) return;
      if (activity.lastWarnTs && now - activity.lastWarnTs < SECTION_STALL_THRESHOLD / 2) {
        return;
      }
      const currentLength = (streamSectionsRef.current?.[key] || "").length;
      if (currentLength === 0 && activity.lastLength === 0) {
        return;
      }
      const pending = streamUpdateQueueRef.current.size;
      const idleForSec = Math.round((now - lastChange) / 1000);
      console.warn(
        `[Plan stream][${getStreamLogId()}][${key}] длина не менялась ${idleForSec}с, очередь=${pending}`
      );
      if (pending > 0) {
        const flushed = forceStreamQueueFlush(`section-stall-${key}`);
        if (!flushed) {
          console.warn(
            `[Plan stream][${getStreamLogId()}] принудительный flush не применил обновления (очередь изменилась)`
          );
        }
      }
      activity.lastWarnTs = now;
    });
  };

  const ensureSectionStallInterval = () => {
    if (sectionStallIntervalRef.current) {
      return;
    }
    sectionStallIntervalRef.current = window.setInterval(
      runImportantSectionStallCheck,
      SECTION_STALL_CHECK_INTERVAL
    );
  };

  const resetStreamBuffers = (reason = "unspecified") => {
    try {
      const logId =
        streamRetryRef.current?.taskId ||
        taskId ||
        planUuid ||
        (currentPlan ? currentPlan.uuid : null) ||
        "unknown";
      // eslint-disable-next-line no-console
      // console.warn(`[Plan stream][${logId}] resetStreamBuffers: ${reason}`);
    } catch (_) {}
    resetChunkWatchdog(`reset-${reason}`);
    stopSectionStallInterval();
    clearStreamQueue();
    if (streamParserRef.current) {
      streamParserRef.current.reset();
    }
    setStreamSections(createEmptyPlanStream());
    lastEventIdRef.current = null;
    streamSectionStatusRef.current = {};
    importantSectionLengthsRef.current = createImportantSectionMap();
    importantSectionActivityRef.current = createImportantSectionActivityMap();
    resetStreamProgress();
    if (sectionStatusIntervalRef.current) {
      clearInterval(sectionStatusIntervalRef.current);
      sectionStatusIntervalRef.current = null;
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      const loc = parseLocation(window.location.pathname);
      const version = getUrlPlanVersion();
      const changedDocument =
        loc.route !== route ||
        String(loc.planUuid || "") !== String(planUuid || "") ||
        version !== editorVersion;
      selectedPlanVersionRef.current = {
        uuid: loc.planUuid || null,
        version,
        loading: false,
      };
      setRoute(loc.route);
      setPlanUuid(loc.planUuid);
      setEditorVersion(version);
      setArticleName(loc.articleName || null);
      if (changedDocument) {
        setPlan(null);
        setCurrentPlan(null);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [route, planUuid, editorVersion]);

  useEffect(() => {
    if (selectedPlanVersionRef.current.loading) {
      return;
    }
    selectedPlanVersionRef.current = {
      uuid: planUuid ? String(planUuid) : null,
      version: editorVersion || null,
      loading: false,
    };
  }, [planUuid, editorVersion]);

  // Load public config flags (feature visibility)
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/config', { silent: true });
        if (res.ok) {
          const data = await res.json();
          setCfgShareEnabled(Boolean(data.shareFeatureEnabled ?? true));
          setCfgBlogEnabled(Boolean(data.blogFeatureEnabled ?? true));
          setCfgShowReviewsEnabled(Boolean(data.showReviewsEnabled ?? true));
        }
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    let active = true;
    let visitorId = readVisitorId() || generateVisitorId();
    const sendPing = async () => {
      try {
        const res = await pingVisitor(visitorId);
        if (!active || !res) return;
        if (res.visitorId && res.visitorId !== visitorId) {
          visitorId = res.visitorId;
          try {
            window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, visitorId);
          } catch (_) {}
        }
      } catch (_) {
        /* silent */
      }
    };
    sendPing();
    const interval = setInterval(sendPing, VISITOR_PING_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  // Parse params passed from blog and optionally autostart generation
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      if (sp.get('from') === 'blog') {
        const dir = sp.get('direction') || '';
        const c = sp.get('city') || '';
        const id = sp.get('idea') || '';
        const funds = sp.get('ownFunds') || '';
        const start = sp.get('start') === '1';
        if (dir) setDirection(dir);
        if (c) setCity(c);
        if (id) setIdea(id);
        if (funds) {
          setOwnFunds(funds);
          setUseOwnFunds(Number(funds) > 0);
        }
        // Clean query from URL for nicer history
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', cleanUrl);
        if (start && id) {
          // Start immediately with captured values to avoid race with setState
          startGeneration(false, {
            direction: dir,
            city: c,
            idea: id,
            ownFunds: funds,
            useOwnFunds: Number(funds) > 0,
          });
        } else if (dir || id) {
          // Open modal prefilled
          setShowModal(true);
        }
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    latestStatusRef.current = status;
  }, [status]);

  useEffect(() => {
    if (status !== "PROCESSING") {
      resetStreamProgress();
    }
  }, [status]);

  useEffect(() => {
    streamSectionStatusRef.current = streamSectionStatus || {};
  }, [streamSectionStatus]);

  useEffect(() => {
    // Watchdog активности: первый чанк секции ждём дольше (до 60с), далее таймаут 15с.
    if (status !== "PROCESSING") {
      resetChunkWatchdog("status-inactive");
      stopSectionStallInterval();
      return;
    }
    ensureSectionStallInterval();
    const activeKey =
      IMPORTANT_STREAM_SECTIONS.find((key) => (streamSectionStatus && streamSectionStatus[key]) === "in_progress") ||
      null;
    if (activeKey && chunkWatchdogRef.current.activeKey !== activeKey) {
      armChunkWatchdogForSection(activeKey, { source: "section-status" });
    }
    if (!activeKey) {
      resetChunkWatchdog("no-active-important");
    }
  }, [streamSectionStatus, status]);

  useEffect(() => {
    // Keep latest applied stream section values in a ref for non-react callbacks
    streamSectionsRef.current = streamSections || {};
  }, [streamSections]);

  useEffect(() => {
    // При возврате вкладки из background переподключаем SSE, если eventId и длины не изменялись дольше порога.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (status === "PROCESSING") {
          const snapshot = {};
          IMPORTANT_STREAM_SECTIONS.forEach((key) => {
            snapshot[key] = (streamSectionsRef.current?.[key] || "").length;
          });
          visibilitySnapshotRef.current = {
            hiddenAt: Date.now(),
            lastEventId: lastEventIdRef.current,
            lengths: snapshot,
          };
        } else {
          visibilitySnapshotRef.current = null;
        }
        return;
      }
      if (document.visibilityState !== "visible") {
        return;
      }
      const snapshot = visibilitySnapshotRef.current;
      visibilitySnapshotRef.current = null;
      if (!snapshot || status !== "PROCESSING") {
        return;
      }
      const hiddenDuration = snapshot.hiddenAt ? Date.now() - snapshot.hiddenAt : 0;
      if (hiddenDuration < VISIBILITY_IDLE_THRESHOLD) {
        return;
      }
      const sameEventId = snapshot.lastEventId === lastEventIdRef.current;
      const sameLengths = IMPORTANT_STREAM_SECTIONS.every((key) => {
        const nowLength = (streamSectionsRef.current?.[key] || "").length;
        return (snapshot.lengths?.[key] || 0) === nowLength;
      });
      if (sameEventId && sameLengths) {
        console.warn(
          `[Plan stream][${getStreamLogId()}] вкладка вернулась из background, поток был неактивен ${Math.round(
            hiddenDuration / 1000
          )}с — переподключаем SSE`
        );
        stopStreamPolling(undefined, { clearQueue: false });
        if (typeof loadSnapshotAndStartRef.current === "function") {
          loadSnapshotAndStartRef.current();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [status, planUuid, taskId]);

  useEffect(() => {
    const logId =
      streamRetryRef.current?.taskId ||
      taskId ||
      planUuid ||
      (currentPlan ? currentPlan.uuid : null) ||
      "unknown";
    IMPORTANT_STREAM_SECTIONS.forEach((key) => {
      const value = (streamSections && streamSections[key]) || "";
      const nextLength = value.length;
      const prevLength = importantSectionLengthsRef.current[key] || 0;
      if (nextLength > 0 && prevLength === 0) {
        importantSectionLengthsRef.current[key] = nextLength;
        // console.log(
        //   `[Plan stream][${logId}][${key}] получены первые данные (${nextLength} символов)`
        // );
      } else if (nextLength > prevLength) {
        importantSectionLengthsRef.current[key] = nextLength;
      } else if (nextLength === 0 && prevLength !== 0) {
        importantSectionLengthsRef.current[key] = 0;
        // console.warn(`[Plan stream][${logId}][${key}] данные очищены`);
      }
      const activity = importantSectionActivityRef.current[key];
      if (activity && activity.lastLength !== nextLength) {
        activity.lastLength = nextLength;
        activity.lastChangeTs = Date.now();
        activity.lastWarnTs = 0;
      }
    });
  }, [streamSections, taskId, planUuid, currentPlan]);

  useEffect(() => {
    const logId =
      streamRetryRef.current?.taskId ||
      taskId ||
      planUuid ||
      (currentPlan ? currentPlan.uuid : null) ||
      "unknown";
    IMPORTANT_STREAM_SECTIONS.forEach((key) => {
      const nextStatus = (streamSectionStatus && streamSectionStatus[key]) || "idle";
      const prevStatus = sectionStatusLogRef.current[key] || "idle";
      if (nextStatus !== prevStatus) {
        sectionStatusLogRef.current[key] = nextStatus;
        // console.log(`[Plan stream][${logId}][${key}] статус: ${nextStatus}`);
        if (nextStatus === "done") {
          const length = importantSectionLengthsRef.current[key] || 0;
          if (length === 0) {
            // console.warn(
            //   `[Plan stream][${logId}][${key}] завершено без полученных данных`
            // );
          } else {
            // console.log(
            //   `[Plan stream][${logId}][${key}] итоговая длина ${length} символов`
            // );
          }
        }
        if (nextStatus === "idle") {
          importantSectionLengthsRef.current[key] = 0;
        }
      }
    });
  }, [streamSectionStatus, taskId, planUuid, currentPlan]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === ACTIVE_TASKS_STORAGE_KEY) {
        try {
          activeTasksRef.current = event.newValue ? JSON.parse(event.newValue) || {} : {};
        } catch (err) {
          activeTasksRef.current = {};
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (route !== "editor" && route !== "title") {
      if (statusInterval.current) {
        clearInterval(statusInterval.current);
        statusInterval.current = null;
      }
      if (streamSource.current) {
        streamSource.current.close();
        streamSource.current = null;
      }
      resetStreamBuffers("route moved away");
      setTaskId(null);
      setLoading(false);
      if (route !== "share") {
        setPlanUuid(null);
      }
    }
  }, [route]);

  useEffect(() => {
    if (route === 'accessDenied') {
      try { if (typeof window !== 'undefined' && typeof window.ym === 'function') window.ym(104041659, 'reachGoal', 'BP_ACCESS_ERROR'); } catch {}
    }
  }, [route]);

  useEffect(() => {
    if (route === "notFound") {
      const currentState = window.history.state || {};
      if (currentState?.statusCode !== 404) {
        window.history.replaceState({ ...currentState, statusCode: 404 }, "", window.location.pathname);
      }
      return;
    }

    let path;
    let fullPath;

    if (route === "editor") {
      path = planUuid ? `/editor/${planUuid}` : "/editor";
      const hash = pendingEditorAnchor ? `#${pendingEditorAnchor}` : window.location.hash;
      const versionQuery = editorVersion ? `?version=${editorVersion}` : "";
      fullPath = `${path}${versionQuery}${hash || ""}`;
    } else if (route === "title") {
      path = planUuid ? `/title/${planUuid}` : "/title";
      fullPath = path;
    } else {
      path = routePaths[route] || "/";
      // The password reset page reads the one-time credentials from the query
      // string. Keep them in the URL until the user leaves this route.
      fullPath = route === "resetPassword"
        ? `${path}${window.location.search}`
        : path;
    }

    const currentUrl = window.location.pathname + window.location.search + window.location.hash;
    if (currentUrl !== fullPath) {
      window.history.pushState({}, "", fullPath);
    } else if ((window.history.state || {}).statusCode === 404) {
      window.history.replaceState({}, "", fullPath);
    }
  }, [route, planUuid, articleName, pendingEditorAnchor, editorVersion]);

  useEffect(() => {
    if (route !== "editor" && pendingEditorAnchor !== null) {
      setPendingEditorAnchor(null);
    }
  }, [route, pendingEditorAnchor]);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) {
      setUser(JSON.parse(stored));
    }
    if (localStorage.getItem("token")) {
      apiFetch("/user/me", { silent: true })
        .then((res) => {
          if (res.ok) return res.json().then(setUser);
          console.info("Current user is not authorized yet, skipping profile load.");
          return null;
        })
        .catch((error) => {
          console.error("Failed to fetch current user", error);
        });
    }
  }, []);

  const applyPlan = (p) => {
    setCurrentPlan(p);
    const nextPlanUuid = p?.uuid || (p?.id ? String(p.id) : null);
    const currentPlanUuid = planUuid ? String(planUuid) : null;
    // Reset stream buffers in applyPlan ONLY when switching to a different plan UUID.
    // Do not reset on intermediate fetches for the same plan or while streaming is active.
    const shouldReset = Boolean(nextPlanUuid && currentPlanUuid && nextPlanUuid !== currentPlanUuid);
    try {
      const logId = nextPlanUuid || currentPlanUuid || "unknown";
      // console.log(
      //   `[Plan stream][${logId}] applyPlan shouldReset=${shouldReset} status=${status} next=${nextPlanUuid} curr=${currentPlanUuid}`
      // );
    } catch (_) {}

    if (shouldReset) {
      resetStreamBuffers("applyPlan-switch");
    }
    setDirection(p.direction);
    setGenerationType(p.generationType || DEFAULT_PLAN_GENERATION_TYPE);
    setIdea(p.idea);
    setCity(p.city || "");
    setPaymentStatus(p.paymentStatus);
    setPaid(p.paymentStatus === "PAID");
    let parsed = null;
    if (p.content) {
      try {
        parsed = JSON.parse(p.content);
      } catch (e) {
        parsed = null;
      }
    }
    setPlan(parsed);
    const isProcessing = p.status === "PROCESSING";
    setLoading(isProcessing);
    setStatus(p.status);
    setOwnFunds(p.ownFunds || 0);
    setUseOwnFunds((p.ownFunds || 0) > 0);
  };

  const notifyGenerationFailed = (taskIdValue, planUuidValue) => {
    const normalizedTaskId = taskIdValue || null;
    const normalizedPlanUuid = planUuidValue || null;
    const prev = generationErrorToastRef.current;
    if (prev.taskId === normalizedTaskId && prev.planUuid === normalizedPlanUuid) {
      return;
    }
    generationErrorToastRef.current = {
      taskId: normalizedTaskId,
      planUuid: normalizedPlanUuid,
    };
    toast.error("Не удалось сгенерировать бизнес-план");
  };

  const refreshPlanByUuid = async (uuid, version = editorVersion) => {
    if (!uuid) return null;
    const requestedVersion = Number.isInteger(version) && version > 0 ? version : null;
    const query = requestedVersion ? `?version=${requestedVersion}` : "";
    const res = await apiFetch(`/plan/by-uuid/${uuid}${query}`, { planUuid: uuid, silent: true });
    if (res.status === 401 || res.status === 403) {
      return null;
    }
    if (!res.ok) {
      return null;
    }
    const p = await res.json();
    const responseVersion = Number.isInteger(p?.version) && p.version > 0
      ? p.version
      : requestedVersion;
    if (!isSelectedPlanVersion(uuid, responseVersion)) {
      return null;
    }
    applyPlan(p);
    if (p.uuid || p.id) {
      setPlanUuid(p.uuid || p.id);
    }
    if (!selectedPlanVersionRef.current.version && responseVersion) {
      selectedPlanVersionRef.current = {
        uuid: String(p.uuid || p.id || uuid),
        version: responseVersion,
      };
      setEditorVersion(responseVersion);
    }
    return p;
  };

  const handleGenerationErrorState = async (taskIdValue, planUuidForTask = planUuid) => {
    if (statusInterval.current) {
      clearInterval(statusInterval.current);
      statusInterval.current = null;
    }
    stopStreamPolling(undefined, { clearQueue: true });
    scheduleStreamFlush({ immediate: true });
    if (planUuidForTask) {
      removeActiveTaskMapping(planUuidForTask);
    }
    setTaskId(null);
    setStatus("ERROR");
    setLoading(false);
    setCurrentPlan((prev) => (prev ? { ...prev, status: "ERROR" } : prev));
    notifyGenerationFailed(taskIdValue, planUuidForTask);
    try {
      await refreshPlanByUuid(planUuidForTask);
    } catch (err) {
      console.error(`[Plan sync][${planUuidForTask || "unknown"}]`, err);
    }
    if (user) {
      fetchPlans({ focusUuid: planUuidForTask });
    }
  };

  const fetchPlans = async () => {
    const res = await apiFetch(`/plans`);
    if (res.ok) {
      const data = await res.json();
      setPlans(data);
      data?.forEach((p) => {
        if (p?.uuid) {
          removePlanAccessCode(p.uuid);
        }
      });
      // This endpoint is only a list. Its content field is the latest variant,
      // therefore it must never be used as editor document data.
    }
  };

  useEffect(() => {
    if (user && route === "account") {
      fetchPlans();
    }
  }, [route, user]);

  useEffect(() => {
    if (route === "share" && planUuid) {
      let cancelled = false;
      setShareProcessing(true);
      setShareRouteError(null);
      (async () => {
        try {
          const res = await apiFetch(`/share/${planUuid}`, { method: "POST" });
          if (!res.ok) {
            if (!cancelled) {
              const message = res.status === 404 ? "Ссылка не активна или план не найден" : "Не удалось открыть ссылку";
              setShareRouteError(message);
            }
            return;
          }
          const newPlan = await res.json();
          if (cancelled) {
            return;
          }
          const newUuid = newPlan.uuid || (newPlan.id ? String(newPlan.id) : null);
          setPlanUuid(newUuid);
          applyPlan(newPlan);
          setRoute("editor");
          // Fetch fresh copy from backend to avoid any cached state
          if (newUuid) {
            try {
              const fres = await apiFetch(`/plan/by-uuid/${newUuid}`, { planUuid: newUuid, silent: true });
              if (fres.status === 401 || fres.status === 403) {
                try { if (typeof window !== 'undefined' && typeof window.ym === 'function') window.ym(104041659, 'reachGoal', 'BP_ACCESS_ERROR'); } catch {}
                setRoute('accessDenied');
              } else if (fres.ok) {
                const fresh = await fres.json();
                applyPlan(fresh);
              }
            } catch (_) {}
            try {
              await fetchPlans({ focusUuid: newUuid });
            } catch (_) {}
          }
        } catch (err) {
          console.error("[Share][open]", err);
          if (!cancelled) {
            setShareRouteError("Не удалось открыть ссылку");
          }
        } finally {
          if (!cancelled) {
            setShareProcessing(false);
          }
        }
      })();
      return () => {
        cancelled = true;
        setShareProcessing(false);
      };
    }
  }, [route, planUuid]);

  useEffect(() => {
    if ((route === "editor" || route === "title") && planUuid && !plan) {
      apiFetch(`/plan/by-uuid/${planUuid}${editorVersion ? `?version=${editorVersion}` : ""}`, { planUuid, silent: true })
        .then((res) => {
          if (res.status === 401 || res.status === 403) {
            try { if (typeof window !== 'undefined' && typeof window.ym === 'function') window.ym(104041659, 'reachGoal', 'BP_ACCESS_ERROR'); } catch {}
            setRoute('accessDenied');
            return null;
          }
          return res.ok ? res.json() : null;
        })
        .then((p) => {
          if (p) {
            const responseVersion = Number.isInteger(p.version) && p.version > 0 ? p.version : editorVersion;
            if (!isSelectedPlanVersion(planUuid, responseVersion)) {
              return;
            }
            applyPlan(p);
            if (!editorVersion && Number.isInteger(p.version) && p.version > 0) {
              selectedPlanVersionRef.current = {
                uuid: String(p.uuid || p.id || planUuid),
                version: p.version,
              };
              setEditorVersion(p.version);
            }
            // Auto-resume/auto-regenerate logic
            safeAutoResumeOrRegenerate(p, { source: 'mount' });
          }
        })
        .catch(() => {});
    }
  }, [route, planUuid, editorVersion]);

  useEffect(() => {
    if (route !== "editor" && route !== "title") {
      loadSnapshotAndStartRef.current = null;
      return;
    }
    if (!planUuid) {
      loadSnapshotAndStartRef.current = null;
      return;
    }
    // Do not attempt snapshot/SSE for non-processing plans
    if (status !== "PROCESSING") {
      loadSnapshotAndStartRef.current = null;
      return;
    }

    const storedTaskId = getActiveTaskId(planUuid);
    if (storedTaskId && taskId !== storedTaskId) {
      setTaskId(storedTaskId);
    }
    const effectiveTaskId = storedTaskId || taskId;
    if (!effectiveTaskId) {
      // TODO: объединить с backend, чтобы можно было запросить taskId по planUuid,
      // если вкладка перезагружена посреди генерации и localStorage недоступен.
      loadSnapshotAndStartRef.current = null;
      return;
    }

    // Ensure we have snapshot loaded before starting SSE
    const loadSnapshotAndStart = async () => {
      try {
        // Pre-check active status by UUID to decide whether to silence snapshot errors
        let snapshotSilent = false;
        try {
          const activeRes = await apiFetch(`/plan/${planUuid}/active`, { planUuid, silent: true });
          if (activeRes.ok) {
            const activeData = await activeRes.json();
            const isActive = !!activeData.active;
            if (!isActive) {
              snapshotSilent = true;
              try { console.info(`[Plan snapshot][${effectiveTaskId}] active=false, calling snapshot silently`); } catch {}
            }
          }
        } catch (_) {}

        const res = await apiFetch(`${API}/${effectiveTaskId}/snapshot`, snapshotSilent ? { silent: true } : undefined);
        if (res.ok) {
          const snap = await res.json();
          // Apply snapshot to stream sections
          try {
            const parser = streamParserRef.current;
            parser?.reset();
            if (snap?.sections) {
              const sections = snap.sections || {};
              const mergedSections = { ...streamSectionsRef.current, ...sections };
              streamSectionsRef.current = mergedSections;
              setStreamSections(mergedSections);
              parser?.hydrate?.(sections, snap?.activeKey || null);
              // Initialize section statuses from backend snapshot
              setStreamSectionStatus(() => {
                const next = {};
                const completed = Array.isArray(snap?.completedSections) ? snap.completedSections : [];
                completed.forEach((k) => { next[k] = 'done'; });
                if (snap?.status === 'PROCESSING' && snap?.activeKey) {
                  next[snap.activeKey] = 'in_progress';
                  streamActiveSectionRef.current = snap.activeKey;
                } else {
                  streamActiveSectionRef.current = null;
                }
                return next;
              });
            } else if (snap?.text) {
              // Fallback: legacy payload with text, parse on client
              parser?.processChunk(snap.text);
              const buffers = parser?.getBuffers?.() || {};
              const mergedSections = { ...streamSectionsRef.current, ...buffers };
              streamSectionsRef.current = mergedSections;
              setStreamSections(mergedSections);
              const keys = Object.keys(buffers).filter((k) => (buffers[k] || '').length > 0);
              if (keys.length > 0) updateStreamSectionStates(keys);
            }
            lastEventIdRef.current = snap?.lastEventId || null;
          } catch (_) {}

          // If generation still in progress, start SSE from lastEventId
          if (snap?.status === 'PROCESSING' && !snap?.completed) {
            startStreamPolling(effectiveTaskId, planUuid);
            // Begin lightweight polling of section statuses from snapshot (completedSections + activeKey)
            if (sectionStatusIntervalRef.current) {
              clearInterval(sectionStatusIntervalRef.current);
              sectionStatusIntervalRef.current = null;
            }
            sectionStatusIntervalRef.current = window.setInterval(async () => {
              try {
                const r = await apiFetch(`${API}/${effectiveTaskId}/snapshot`);
                if (!r.ok) return;
                const s = await r.json();
                setStreamSectionStatus(() => {
                  const next = {};
                  const completed = Array.isArray(s?.completedSections) ? s.completedSections : [];
                  completed.forEach((k) => { next[k] = 'done'; });
                  if (s?.status === 'PROCESSING' && s?.activeKey) {
                    next[s.activeKey] = 'in_progress';
                    streamActiveSectionRef.current = s.activeKey;
                  }
                  return next;
                });
                if (s?.lastEventId) lastEventIdRef.current = s.lastEventId;
              } catch (_) {}
            }, 2500);
          }
        } else if (snapshotSilent) {
          try { console.warn(`[Plan snapshot][${effectiveTaskId}] failed with status=${res.status} (silent due to active=false)`); } catch {}
        }
      } catch (_) {}
    };

    if (!statusInterval.current) {
      pollStatus(effectiveTaskId, planUuid, editorVersion);
    }
    // Start/restart SSE from snapshot baseline
    loadSnapshotAndStart();
    loadSnapshotAndStartRef.current = loadSnapshotAndStart;
    return () => {
      if (loadSnapshotAndStartRef.current === loadSnapshotAndStart) {
        loadSnapshotAndStartRef.current = null;
      }
    };
  }, [route, planUuid, status, taskId]);

  const renamePlan = async (id, title) => {
    await apiFetch(`${API}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title }),
    });
    fetchPlans();
  };

  const deletePlan = async (id) => {
    await apiFetch(`${API}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ archived: true }),
    });
    fetchPlans();
  };

  const resetRetryState = (taskIdValue = null) => {
    if (streamRetryRef.current.timer) {
      clearTimeout(streamRetryRef.current.timer);
    }
    streamRetryRef.current = { attempts: 0, timer: null, taskId: taskIdValue };
  };

  const closeStreamSource = (source) => {
    if (!source) return;
    source.onmessage = null;
    source.onerror = null;
    source.onopen = null;
    try {
      source.close();
    } catch (err) {
      // ignore close errors
    }
    if (streamSource.current === source) {
      streamSource.current = null;
    }
  };

  const stopStreamPolling = (source, { clearQueue = false } = {}) => {
    const current = source || streamSource.current;
    if (current) {
      closeStreamSource(current);
    }
    if (clearQueue) {
      clearStreamQueue();
    }
    resetRetryState(null);
    resetChunkWatchdog("stream-stop");
  };

  const startStreamPolling = (id, planUuidForTask = planUuid) => {
    stopStreamPolling(undefined, { clearQueue: true });
    if (!id) return;

    const normalizedBase = API_BASE_URL ? API_BASE_URL.replace(/\/$/, "") : "";

    resetRetryState(id);

    const scheduleReconnect = () => {
      if (streamRetryRef.current.timer) {
        clearTimeout(streamRetryRef.current.timer);
      }
      const attempt = streamRetryRef.current.attempts + 1;
      streamRetryRef.current.attempts = attempt;
      const delay = Math.min(15000, 1000 * Math.pow(2, attempt - 1));
      streamRetryRef.current.timer = window.setTimeout(() => {
        streamRetryRef.current.timer = null;
        if (streamRetryRef.current.taskId !== id || latestStatusRef.current !== "PROCESSING") {
          resetRetryState(null);
          return;
        }
        connect();
      }, delay);
    };

    const connect = () => {
      if (streamRetryRef.current.taskId !== id) {
        return;
      }
      try {
        const lastEventId = lastEventIdRef.current || null;
        const query = lastEventId ? `?lastEventId=${encodeURIComponent(lastEventId)}` : "";
        const path = `${API}/${id}/stream${query}`;
        const url = normalizedBase ? `${normalizedBase}${path}` : path;
        const source = new EventSource(url);
        streamSource.current = source;

        source.onopen = () => {
          streamRetryRef.current.attempts = 0;
        };

        source.onmessage = (event) => {
          if (!event.data) return;
          try {
            const payload = JSON.parse(event.data);
            const lastId = event.lastEventId || null;
            if (lastId) lastEventIdRef.current = lastId;

            if (payload.activeKey && PLAN_STREAM_SECTION_KEYS.includes(payload.activeKey)) {
              updateStreamSectionStates([payload.activeKey]);
            }
            if (payload.completedSection) {
              completeStreamSection(payload.completedSection);
            }

            if (payload.chunk) {
              let updates = [];
              const sectionKey = payload.sectionKey;
              if (sectionKey && PLAN_STREAM_SECTION_KEYS.includes(sectionKey)) {
                const queued = streamUpdateQueueRef.current.get(sectionKey);
                const displayed = streamSectionsRef.current?.[sectionKey] || "";
                const baseline = typeof queued === "string" ? queued : displayed;
                updates = [{ key: sectionKey, value: baseline + String(payload.chunk) }];
              } else {
                // Backward-compatible parsing for streams produced during a rolling deployment.
                const parser = streamParserRef.current;
                updates = parser ? parser.processChunk(payload.chunk) : [];
              }
                if (updates.length > 0) {
                  updateStreamSectionStates(updates.map(({ key }) => key));
                  enqueueStreamUpdates(updates);
                  const hasTimer = streamFlushTimerRef.current !== null;
                  scheduleStreamFlush({ immediate: !hasTimer });
                } else if (streamUpdateQueueRef.current.size > 0) {
                  const hasTimer = streamFlushTimerRef.current !== null;
                  scheduleStreamFlush({ immediate: !hasTimer });
                }
              handleChunkWatchdogActivity("sse-chunk");
            }
            if (payload.completed || payload.status !== "PROCESSING") {
              const terminalStatus = payload.status || null;
              IMPORTANT_STREAM_SECTIONS.forEach((key) => {
                const sectionStatus =
                  streamSectionStatusRef.current?.[key] || sectionStatusLogRef.current[key] || "idle";
                // if (sectionStatus === "idle") console.warn(`[Plan stream][${id}][${key}] поток завершён без данных`);
              });
              resetChunkWatchdog("stream-complete");
              finalizeActiveStreamSection();
              stopStreamPolling(source);
              scheduleStreamFlush({ immediate: true });
              try {
                lastEventIdRef.current = null;
                if (sectionStatusIntervalRef.current) {
                  clearInterval(sectionStatusIntervalRef.current);
                  sectionStatusIntervalRef.current = null;
                }
              } catch (_) {}
              if (terminalStatus) {
                setStatus(terminalStatus);
              }
              if (terminalStatus === "ERROR") {
                void handleGenerationErrorState(id, planUuidForTask);
              }
            }
          } catch (err) {
            // console.warn(`[Plan stream][${id}] Не удалось разобрать SSE-пакет`, err, event.data);
          }
        };

        source.onerror = (err) => {
          if (streamSource.current !== source) {
            return;
          }
          console.error(
            `[Plan stream][${id}] ошибка подключения (state ${source.readyState})`,
            err
          );
          closeStreamSource(source);
          if (streamRetryRef.current.taskId !== id || latestStatusRef.current !== "PROCESSING") {
            resetRetryState(null);
            return;
          }
          scheduleReconnect();
        };
      } catch (err) {
        console.error("Не удалось открыть поток генерации", err);
        if (streamRetryRef.current.taskId !== id || latestStatusRef.current !== "PROCESSING") {
          resetRetryState(null);
          return;
        }
        scheduleReconnect();
      }
    };

    connect();
  };

  const retryPlan = async (p) => {
    stopStreamPolling(undefined, { clearQueue: true });
    resetStreamBuffers("retryPlan");
    lockPlanContentUntilVersionIsSelected(p?.uuid);
    if (p?.uuid) {
      removeActiveTaskMapping(p.uuid);
    }
    setDirection(p.direction);
    setGenerationType(p.generationType || DEFAULT_PLAN_GENERATION_TYPE);
    setIdea(p.idea);
    setCity(p.city || "");
    setUseOwnFunds((p.ownFunds || 0) > 0);
    setOwnFunds(p.ownFunds || "");
    setShowModal(false);
    setShowSettings(false);
    setPlanUuid(null);
    setLoading(true);
    setPlan(null);
    setCurrentPlan(null);
    setPaid(false);
    setPaymentStatus("NOT_PAID");
    setStatus("PROCESSING");
    try {
      const res = await apiFetch(`${API}/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          direction: p.direction,
          generationType: p.generationType || DEFAULT_PLAN_GENERATION_TYPE,
          idea: p.idea,
          city: p.city || null,
          ownFunds: p.ownFunds || 0,
          userId: user ? user.id : null,
        }),
      });
      const { id, planUuid: newPlanUuid, accessCode, version } = await res.json();
      setTaskId(id);
      selectPlanVersion(newPlanUuid, version || 1);
      setRoute("editor");
      if (accessCode && newPlanUuid) {
        setPlanAccessCode(newPlanUuid, accessCode);
      }
      if (newPlanUuid) {
        saveActiveTaskMapping(newPlanUuid, id);
      }
      pollStatus(id, newPlanUuid, version || 1);
      if (user) {
        fetchPlans({ focusUuid: newPlanUuid });
      }
    } catch (e) {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAuthErrorHandler(() => {
      setUser(null);
      setShowLogin(true);
    });
  }, []);

  useEffect(() => {
    if (!user) {
      setPlans([]);
      setCurrentPlan(null);
      return;
    }
    if (shareProcessing) {
      return;
    }
    if (taskId) {
      apiFetch(`${API}/${taskId}/attach`, { method: "POST", silent: true })
        .then(fetchPlans)
        .catch(() => {});
    } else if (planUuid) {
      apiFetch(`/plan/${planUuid}/attach-user`, { method: "POST", planUuid, silent: true })
        .then((res) => {
          if (res.ok) {
            removePlanAccessCode(planUuid);
          }
          fetchPlans({ focusUuid: planUuid });
        })
        .catch(() => {});
    } else {
      fetchPlans();
    }
  }, [user, shareProcessing, taskId, planUuid]);

  useEffect(() => {
    return () => {
      if (statusInterval.current) {
        clearInterval(statusInterval.current);
        statusInterval.current = null;
      }
      stopStreamPolling(undefined, { clearQueue: true });
      resetChunkWatchdog("unmount");
      stopSectionStallInterval();
    };
  }, []);

  const startGeneration = async (regenerate = false, prefill = null) => {
    const currentUuid = regenerate ? planUuid : null;
    if (currentUuid) {
      lockPlanContentUntilVersionIsSelected(currentUuid);
    }
    const dir = prefill && prefill.direction != null ? prefill.direction : direction;
    const genType =
      prefill && prefill.generationType != null ? prefill.generationType : generationType;
    const ideaVal = prefill && prefill.idea != null ? prefill.idea : idea;
    const ct = prefill && prefill.city != null ? prefill.city : city;
    const ofRaw = prefill && prefill.ownFunds != null ? prefill.ownFunds : ownFunds;
    const useFunds = prefill && prefill.useOwnFunds != null ? prefill.useOwnFunds : useOwnFunds;
    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        direction: dir,
        generationType: genType,
        idea: ideaVal,
        city: ct || null,
        ownFunds: useFunds ? parseInt(ofRaw || "0", 10) : 0,
        userId: user ? user.id : null,
        planUuid: currentUuid,
      }),
    };
    if (currentUuid) {
      options.planUuid = currentUuid;
    }
    try {
      const res = await apiFetch(`${API}/start`, options);
      if (!res.ok) {
        if (currentUuid) {
          selectPlanVersion(currentUuid, editorVersion);
        }
        if (!regenerate) setLoading(false);
        return;
      }
      const { id: task, planUuid: newPlanUuid, accessCode, version } = await res.json();
      // Reset UI state only after a successful start to keep the user on the current page when backend rejects the request.
      setShowModal(false);
      setShowSettings(false);
      stopStreamPolling(undefined, { clearQueue: true });
      resetStreamBuffers("startGeneration");
      setLoading(true);
      setPlan(null);
      setCurrentPlan(null);
      setPaid(false);
      setPaymentStatus("NOT_PAID");
      setStatus("PROCESSING");
      // When regeneration starts, make share link inactive in UI immediately
      setShareInfo((prev) => (prev ? { ...prev, isActive: false } : prev));
      if (planUuid) {
        removeActiveTaskMapping(planUuid);
      }
      setTaskId(task);
      const planUuidForTask = newPlanUuid || currentUuid || null;
      selectPlanVersion(planUuidForTask, version || 1);
      setRoute("editor");
      if (accessCode && newPlanUuid) {
        setPlanAccessCode(newPlanUuid, accessCode);
      }
      if (planUuidForTask) {
        saveActiveTaskMapping(planUuidForTask, task);
      }
      pollStatus(task, planUuidForTask, version || 1);
      if (user) {
        fetchPlans({ focusUuid: planUuidForTask });
      }
    } catch (e) {
      if (regenerate) {
        if (currentUuid) {
          selectPlanVersion(currentUuid, editorVersion);
        }
        return;
      }
      setTimeout(() => {
        setLoading(false);
        setPlan({ summary: "Демо" });
      }, 1200);
    }
  };

  // Repeat regeneration for current plan using backend's stored fields.
  const repeatRegeneration = async () => {
    if (!planUuid) return;
    // Reset UI state and prepare for streaming
    setShowSettings(false);
    stopStreamPolling(undefined, { clearQueue: true });
    resetStreamBuffers("repeatRegeneration");
    lockPlanContentUntilVersionIsSelected(planUuid);
    setLoading(true);
    setPlan(null);
    setCurrentPlan(null);
    setPaid(false);
    setPaymentStatus("NOT_PAID");
    setStatus("PROCESSING");
    try {
      const res = await apiFetch(`/plan/${planUuid}/regenerate`, { method: 'POST', planUuid });
      if (!res.ok) {
        setLoading(false);
        return;
      }
      const { id, planUuid: newPlanUuid, accessCode, version } = await res.json();
      setTaskId(id);
      const planUuidForTask = newPlanUuid || planUuid;
      selectPlanVersion(planUuidForTask, version || 1);
      setRoute("editor");
      if (accessCode && planUuidForTask) {
        setPlanAccessCode(planUuidForTask, accessCode);
      }
      if (planUuidForTask) {
        saveActiveTaskMapping(planUuidForTask, id);
      }
      pollStatus(id, planUuidForTask, version || 1);
      if (user) {
        fetchPlans({ focusUuid: planUuidForTask });
      }
    } catch (_) {
      setLoading(false);
    }
  };

  const openPlanVersion = async (version) => {
    if (!planUuid || !Number.isInteger(version) || version < 1) return;
    if (statusInterval.current) {
      clearInterval(statusInterval.current);
      statusInterval.current = null;
    }
    stopStreamPolling(undefined, { clearQueue: true });
    resetStreamBuffers("openPlanVersion");
    setPlan(null);
    setCurrentPlan(null);
    selectPlanVersion(planUuid, version);
    setRoute("editor");
    await refreshPlanByUuid(planUuid, version);
  };

  const savePlanVersionContent = async (content) => {
    const targetVersion = editorVersion || currentPlan?.version || 1;
    if (!planUuid || !targetVersion) return false;
    const res = await apiFetch(`/plan/${planUuid}?version=${targetVersion}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
      planUuid,
    });
    if (!res.ok) return false;
    const updated = await res.json();
    if (isSelectedPlanVersion(planUuid, updated.version || targetVersion)) {
      selectPlanVersion(planUuid, targetVersion);
      applyPlan(updated);
    }
    return true;
  };

  const refreshExtraGenerationCredits = async () => {
    if (!planUuid) return;
    window.setTimeout(async () => {
      await refreshPlanByUuid(planUuid, editorVersion);
    }, 1200);
  };

  const pollStatus = (id, planUuidForTask = planUuid, taskVersion = editorVersion) => {
    if (!id) return;
    if (statusInterval.current) {
      clearInterval(statusInterval.current);
      statusInterval.current = null;
    }
    stopStreamPolling(undefined, { clearQueue: true });
    setTaskId(id);
    if (planUuidForTask) {
      saveActiveTaskMapping(planUuidForTask, id);
    }
    // SSE will be started via snapshot loader to resume precisely from lastEventId

    const fetchStatus = async () => {
      try {
        const res = await apiFetch(`${API}/${id}/status`);
        if (!res.ok) {
          if (res.status === 404) {
            if (statusInterval.current) {
              clearInterval(statusInterval.current);
              statusInterval.current = null;
            }
            stopStreamPolling(undefined, { clearQueue: true });
            if (planUuidForTask) {
              removeActiveTaskMapping(planUuidForTask);
            }
            setTaskId(null);
            setLoading(false);
          }
          return;
        }
        const data = await res.json();
        setStatus(data.status);
        if (data.status === "DONE" || data.status === "ERROR") {
          if (statusInterval.current) {
            clearInterval(statusInterval.current);
            statusInterval.current = null;
          }
          stopStreamPolling();
          scheduleStreamFlush({ immediate: true });
          if (planUuidForTask) {
            removeActiveTaskMapping(planUuidForTask);
          }
          setTaskId(null);
        }
        if (data.status === "DONE") {
          setLoading(false);
          // The task endpoint returns the root plan content (latest variant). Use
          // the persisted version endpoint instead, bound to the task's version.
          if (planUuidForTask) {
            await refreshPlanByUuid(planUuidForTask, taskVersion || 1);
          }
          // Keep focus on the plan that just finished to avoid reverting to another plan
          if (user) fetchPlans({ focusUuid: planUuidForTask });
        } else if (data.status === "ERROR") {
          await handleGenerationErrorState(id, planUuidForTask);
        }
      } catch (err) {
        console.error(`[Plan status][${id}]`, err);
      }
    };

    fetchStatus();
    statusInterval.current = window.setInterval(fetchStatus, 5000);
  };

  const navigate = useCallback((to, options = {}) => {
    if ((to === "editor" || to === "title") && options.planUuid) {
      setPlanUuid(options.planUuid);
    }
    setRoute(to);
  }, []);
  const handleEditorAnchorHandled = (anchorKey) => {
    if (!anchorKey) return;
    setPendingEditorAnchor((prev) => (prev === anchorKey ? null : prev));
  };
  const openPlan = (p) => {
    selectPlanVersion(p.uuid, null);
    setPlan(null);
    setCurrentPlan(null);
    setRoute("editor");
    // Auto-resume/auto-regenerate logic when user opens a plan from list
    safeAutoResumeOrRegenerate(p, { source: 'openPlan' });
  };
  const handleLogin = () => setShowLogin(true);
  const handleAccount = () => setRoute("account");
  const handleForgotPasswordLink = () => {
    setShowLogin(false);
    setRoute("forgotPassword");
  };
  const handleLogout = () => {
    logout();
    setUser(null);
    setPlans([]);
    setArticleName(null);
    setRoute("home");
  };

  const handlePaymentSuccess = async (planUuid) => {
    await apiFetch(`/plan/${planUuid}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paid: true, paymentStatus: "PAID" }),
      planUuid,
    });
    const res = await apiFetch(`/plans`);
    if (res.ok) {
      const data = await res.json();
      setPlans(data);
      const updated = data.find((p) => p.uuid === planUuid);
      if (updated) {
        setCurrentPlan(updated);
        setPaid(updated.paymentStatus === "PAID");
        setPaymentStatus(updated.paymentStatus);
        setStatus(updated.status);
      }
    }
    setPaidPlanUuid(planUuid);
    setShowSuccess(true);
  };

  const handleOpenShare = async () => {
    if (!planUuid) return;
    if (status !== "DONE") {
      toast.error("Ссылка доступна после завершения генерации");
      return;
    }
    setShareLoading(true);
    try {
      let res = await apiFetch(`/plan/${planUuid}/share`, { planUuid });
      if (res.status === 403) {
        const code = window.prompt('Введите код доступа к плану');
        if (!code) return;
        try { setPlanAccessCode(planUuid, code.trim()); } catch {}
        res = await apiFetch(`/plan/${planUuid}/share`, { planUuid });
        if (res.status === 403) {
          toast.error("Неверный код доступа или недостаточно прав");
          return;
        }
      }
      if (res.ok) {
        const data = await res.json();
        setShareInfo(data);
      } else {
        setShareInfo({ planUuid, isActive: false, activatedAt: null });
      }
      setShowShareModal(true);
    } catch (err) {
      console.error("[Share]", err);
      toast.error("Не удалось получить данные для ссылки");
    } finally {
      setShareLoading(false);
    }
  };

  const loginModalElement = showLogin ? (
    <LoginModal
      onClose={() => setShowLogin(false)}
      onAuth={(u) => {
        setUser(u);
        // After successful login from reset-password success page, go to account
        if (route === "resetPasswordSuccess") {
          setRoute("account");
        }
      }}
      onPolicy={() => setRoute("policy")}
      onOffer={() => setRoute("offer")}
      onForgotPassword={handleForgotPasswordLink}
    />
  ) : null;

  // --- Auto resume / auto regeneration ---
  const safeAutoResumeOrRegenerate = async (planObj, { source = 'unknown' } = {}) => {
    try {
      if (!planObj || !planObj.uuid) return;
      const uuid = planObj.uuid;
      const statusNow = planObj.status;
      const tabId = tabIdRef.current;

      // Detailed decision logging for troubleshooting
      try { console.info(`[AutoRegen][${uuid}][${source}][tab:${tabId}] status=${statusNow}`); } catch {}

      // No auto-regeneration for NEED_REGENERATE; user must trigger explicitly
      if (statusNow === 'NEED_REGENERATE') {
        try { console.info(`[AutoRegen][${uuid}] status=NEED_REGENERATE (no auto-run)`); } catch {}
        return;
      }

      // Detect hung PROCESSING: status PROCESSING but no active task
      if (statusNow === 'PROCESSING') {
        // Read mapping to see if we already know an active task id for this UUID
        const mappedId = getActiveTaskId(uuid);
        if (mappedId) {
          try { console.info(`[AutoRegen][${uuid}] mapped active task ${mappedId} — resume via snapshot/SSE will handle`); } catch {}
          return;
        }
        const res = await apiFetch(`/plan/${uuid}/active`, { planUuid: uuid, silent: true });
        if (!res.ok) {
          try { console.warn(`[AutoRegen][${uuid}] /active returned ${res.status} — skip`); } catch {}
          return;
        }
        const data = await res.json();
        const isActive = !!data.active;
        try { console.info(`[AutoRegen][${uuid}] active=${isActive}`); } catch {}
        if (!isActive) {
          if (isAutoRegenGuardActive(uuid)) {
            const g = getAutoRegenGuard(uuid);
            try { console.info(`[AutoRegen][${uuid}] guard active (reason=${g?.reason}) — skip`); } catch {}
            return;
          }
          try { console.warn(`[AutoRegen][${uuid}] trigger: HUNG_PROCESS (PROCESSING without active task)`); } catch {}
          setAutoRegenGuard(uuid, 'HUNG_PROCESS');
          await triggerRegeneration(uuid, { reason: 'HUNG_PROCESS' });
        }
      }
    } catch (err) {
      try { console.error('[AutoRegen][error]', err); } catch {}
    }
  };

  const triggerRegeneration = async (uuid, { reason = 'unknown' } = {}) => {
    try {
      const res = await apiFetch(`/plan/${uuid}/regenerate`, { method: 'POST', planUuid: uuid });
      if (!res.ok) {
        try { console.warn(`[AutoRegen][${uuid}] regenerate failed status=${res.status} reason=${reason}`); } catch {}
        return;
      }
      const { id, planUuid: newPlanUuid, accessCode, version } = await res.json();
      const task = id;
      const planUuidForTask = newPlanUuid || uuid;
      try { console.info(`[AutoRegen][${uuid}] regenerate started taskId=${task} reason=${reason}`); } catch {}
      setTaskId(task);
      selectPlanVersion(planUuidForTask, version || 1);
      if (accessCode && planUuidForTask) {
        setPlanAccessCode(planUuidForTask, accessCode);
      }
      if (planUuidForTask) {
        saveActiveTaskMapping(planUuidForTask, task);
      }
      pollStatus(task, planUuidForTask, version || 1);
      if (user) {
        fetchPlans({ focusUuid: planUuidForTask });
      }
    } catch (err) {
      try { console.error(`[AutoRegen][${uuid}] regenerate exception`, err); } catch {}
    }
  };

  const handleActivateShare = async () => {
    if (!planUuid) return;
    setShareActivating(true);
    try {
      let endpoint = `/plan/${planUuid}/share/activate`;
      if (shareInfo?.isActive) {
        endpoint = `/plan/${planUuid}/share/deactivate`;
      } else if (status !== "DONE") {
        toast.error("Активация доступна только для готового плана");
        return;
      }
      let res = await apiFetch(endpoint, { method: "POST", planUuid });
      if (res.status === 403) {
        const code = window.prompt('Введите код доступа к плану');
        if (!code) return;
        try { setPlanAccessCode(planUuid, code.trim()); } catch {}
        res = await apiFetch(endpoint, { method: "POST", planUuid });
        if (res.status === 403) {
          toast.error("Неверный код доступа или недостаточно прав");
          return;
        }
      }
      if (!res.ok) {
        toast.error("Не удалось выполнить действие со ссылкой");
        return;
      }
      const data = await res.json();
      setShareInfo(data);
      toast.success(data.isActive ? "Ссылка активирована" : "Ссылка отключена");
    } catch (err) {
      console.error("[Share][toggle]", err);
      toast.error("Не удалось выполнить действие со ссылкой");
    } finally {
      setShareActivating(false);
    }
  };

  const handleOpenShareSite = () => {
    if (!cfgShareEnabled) return;
    setShowShareSiteModal(true);
  };

  // Auto-regeneration is disabled. Plans are regenerated only by explicit user actions.

  const shareSiteModal = cfgShareEnabled && showShareSiteModal ? (
    <ShareSiteModal onClose={() => setShowShareSiteModal(false)} />
  ) : null;

  if (route === "share") {
    return (
      <>
        <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-16">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow">
            {shareProcessing && !shareRouteError ? (
              <>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
                <h1 className="mt-4 text-lg font-semibold text-slate-900">Подготавливаем копию</h1>
                <p className="mt-2 text-sm text-slate-600">
                  Создаем копию бизнес-плана. Пожалуйста, подождите...
                </p>
              </>
            ) : (
              <>
                <h1 className="text-lg font-semibold text-slate-900">Ссылка недоступна</h1>
                <p className="mt-2 text-sm text-slate-600">{shareRouteError || "Не удалось открыть ссылку."}</p>
                <button
                  type="button"
                  onClick={() => setRoute("home")}
                  className="mt-6 inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
                >
                  На главную
                </button>
              </>
            )}
          </div>
        </div>
        <Toaster position="top-right" />
      </>
    );
  }

  if (route === "accessDenied") {
    return (
      <>
        <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-16">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
              {/* icon placeholder */}
              !
            </div>
            <h1 className="mt-2 text-lg font-semibold text-slate-900">Доступ запрещен</h1>
            <p className="mt-2 text-sm text-slate-600">
              Бизнес-план был создан в другом браузере или другим пользователем. Доступ запрещен.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setRoute("home")}
                className="h-11 rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold shadow hover:bg-blue-700"
              >
                На главную
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (route === "title") {
    return (
      <>
        <TitlePage
          user={user}
          plan={currentPlan}
          planUuid={planUuid}
          version={editorVersion || currentPlan?.version || 1}
          status={status}
          paid={paid}
          onBack={(anchorKey) => {
            const nextAnchor = anchorKey || null;
            if (typeof window !== "undefined") {
              const basePath = planUuid ? `/editor/${planUuid}` : "/editor";
              const versionQuery = editorVersion ? `?version=${editorVersion}` : "";
              const nextUrl = nextAnchor ? `${basePath}${versionQuery}#${nextAnchor}` : `${basePath}${versionQuery}`;
              window.history.replaceState(window.history.state, "", nextUrl);
            }
            setPendingEditorAnchor(nextAnchor);
            setRoute("editor");
          }}
          onLogin={handleLogin}
          onAccount={handleAccount}
          onSaved={(p) => {
            setCurrentPlan(p);
            if (user) {
              fetchPlans();
            }
          }}
          onBuy={() => setOpenPay(true)}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {openPay && (
          <PaymentModal
            onClose={() => setOpenPay(false)}
            onPay={() => setOpenPay(false)}
            onPolicy={() => setRoute("policy")}
            onOffer={() => setRoute("offer")}
            user={user}
            onAuth={(u) => setUser(u)}
            planUuid={planUuid}
            onSuccess={handlePaymentSuccess}
          />
        )}
        {loginModalElement}
        {showSuccess && (
          <PaymentSuccessModal
            planUuid={paidPlanUuid}
            planStatus={status}
            onClose={() => setShowSuccess(false)}
            onContinue={() => setShowSuccess(false)}
          />
        )}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }

  if (route === "notFound") {
    return (
      <>
        <NotFoundPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }

  if (route === "editor" && !planUuid) {
    return (
      <>
        <NotFoundPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }

  if (route === "editor") {
    const shareLink = planUuid
      ? `${typeof window !== "undefined" && window.location ? window.location.origin : ""}/share/${planUuid}`
      : "";
    const shareIdea = direction || idea;
    return (
      <>
        <EditorPage
          idea={idea}
          setIdea={setIdea}
          direction={direction}
          setDirection={setDirection}
          generationType={generationType}
          setGenerationType={setGenerationType}
          loading={loading}
          paid={paid}
          paymentStatus={paymentStatus}
          plan={plan}
          currentPlan={currentPlan}
          planUuid={planUuid}
          version={editorVersion || currentPlan?.version || 1}
          status={status}
          streamSections={streamSections}
          streamSectionStatus={streamSectionStatus}
          initialAnchorKey={pendingEditorAnchor}
          onAnchorHandled={handleEditorAnchorHandled}
          onBuy={() => setOpenPay(true)}
          onBack={() => setRoute("home")}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          onUserUpdate={setUser}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          useOwnFunds={useOwnFunds}
          setUseOwnFunds={setUseOwnFunds}
          ownFunds={ownFunds}
          setOwnFunds={setOwnFunds}
          onRegenerate={() => startGeneration(true)}
          onRepeatRegenerate={repeatRegeneration}
          onOpenVersion={openPlanVersion}
          onSaveContent={savePlanVersionContent}
          onExtraPaymentSuccess={refreshExtraGenerationCredits}
          onTitle={() => setRoute("title")}
          onShare={cfgShareEnabled ? handleOpenShare : null}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
          shareLoading={shareLoading}
          showReviewsEnabled={cfgShowReviewsEnabled}
        >
          {openPay && (
            <PaymentModal
              onClose={() => setOpenPay(false)}
              onPay={() => setOpenPay(false)}
              onPolicy={() => setRoute("policy")}
              onOffer={() => setRoute("offer")}
              user={user}
              onAuth={(u) => setUser(u)}
              planUuid={planUuid}
              onSuccess={handlePaymentSuccess}
            />
          )}
        </EditorPage>
        {showShareModal && (
          <ShareModal
            onClose={() => setShowShareModal(false)}
            shareUrl={shareLink}
            idea={shareIdea}
            isActive={shareInfo?.isActive ?? false}
            activatedAt={shareInfo?.activatedAt ?? null}
            onActivate={handleActivateShare}
            activating={shareActivating}
          />
        )}
        {shareSiteModal}
        {loginModalElement}
        {showSuccess && (
          <PaymentSuccessModal
            planUuid={paidPlanUuid}
            planStatus={status}
            onClose={() => setShowSuccess(false)}
            onContinue={() => setShowSuccess(false)}
          />
        )}
        <Toaster position="top-right" />
      </>
    );
  }

  if (route === "faq") {
    return (
      <>
        <FaqPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }
  if (route === "support") {
    return (
      <>
        <SupportPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }
  if (route === "policy") {
    return (
      <>
        <PrivacyPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }
  if (route === "offer") {
    return (
      <>
        <OfferPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }
  if (route === "tariffs") {
    return <><TariffsPage navigate={navigate} user={user} onLogin={handleLogin} onAccount={handleAccount} showBlog={cfgBlogEnabled} onShareSite={cfgShareEnabled ? handleOpenShareSite : null} />{loginModalElement}{shareSiteModal}<Toaster position="top-right" /></>;
  }
  if (route === "forgotPassword") {
    return (
      <>
        <ForgotPasswordPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }
  if (route === "forgotPasswordSent") {
    return (
      <>
        <ForgotPasswordSentPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }
  if (route === "resetPassword") {
    return (
      <>
        <ResetPasswordPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }
  if (route === "resetPasswordSuccess") {
    return (
      <>
        <ResetPasswordSuccessPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }
  if (route === "resetPasswordInvalid") {
    return (
      <>
        <ResetPasswordInvalidPage
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }
  if (route === "settings") {
    return (
      <>
        <SettingsPage
          user={user}
          navigate={navigate}
          onLogout={handleLogout}
          onUserUpdate={setUser}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }
  if (route === "account") {
    return (
      <>
        <AccountPage
          plans={plans}
          navigate={navigate}
          onEdit={openPlan}
          onRetry={retryPlan}
          onDelete={deletePlan}
          onRename={renamePlan}
          onLogout={handleLogout}
          idea={idea}
          setIdea={setIdea}
          direction={direction}
          setDirection={setDirection}
          generationType={generationType}
          setGenerationType={setGenerationType}
          city={city}
          setCity={setCity}
          showModal={showModal}
          setShowModal={setShowModal}
          onStart={startGeneration}
          useOwnFunds={useOwnFunds}
          setUseOwnFunds={setUseOwnFunds}
          ownFunds={ownFunds}
          setOwnFunds={setOwnFunds}
          onPaymentSuccess={handlePaymentSuccess}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {showSuccess && (
          <PaymentSuccessModal
            planUuid={paidPlanUuid}
            planStatus={status}
            onClose={() => setShowSuccess(false)}
            onContinue={() => {
              setShowSuccess(false);
              const p = plans.find((pp) => pp.uuid === paidPlanUuid) || currentPlan;
              if (p) {
                // openPlan sets route to editor with the selected plan
                setCurrentPlan(p);
                setDirection(p.direction);
                setGenerationType(p.generationType || DEFAULT_PLAN_GENERATION_TYPE);
                setIdea(p.idea);
                setCity(p.city || "");
                setPaymentStatus(p.paymentStatus);
                setPaid(p.paymentStatus === "PAID");
                setPlan(JSON.parse(p.content));
                setLoading(false);
                setStatus(p.status);
                setOwnFunds(p.ownFunds || 0);
                setUseOwnFunds((p.ownFunds || 0) > 0);
                setRoute("editor");
              } else {
                setRoute("editor");
              }
            }}
          />
        )}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }

  if (route === "examples") {
    return (
      <>
        <ExamplesPage
          idea={idea}
          setIdea={setIdea}
          direction={direction}
          setDirection={setDirection}
          generationType={generationType}
          setGenerationType={setGenerationType}
          city={city}
          setCity={setCity}
          showModal={showModal}
          setShowModal={setShowModal}
          onStart={startGeneration}
          useOwnFunds={useOwnFunds}
          setUseOwnFunds={setUseOwnFunds}
          ownFunds={ownFunds}
          setOwnFunds={setOwnFunds}
          navigate={navigate}
          user={user}
          onLogin={handleLogin}
          onAccount={handleAccount}
          showBlog={cfgBlogEnabled}
          onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
        />
        {loginModalElement}
        {shareSiteModal}
        <Toaster position="top-right" />
      </>
    );
  }

  return (
    <>
      <HomePage
        idea={idea}
        setIdea={setIdea}
        direction={direction}
        setDirection={setDirection}
        generationType={generationType}
        setGenerationType={setGenerationType}
        city={city}
        setCity={setCity}
        showModal={showModal}
        setShowModal={setShowModal}
        onStart={startGeneration}
        useOwnFunds={useOwnFunds}
        setUseOwnFunds={setUseOwnFunds}
        ownFunds={ownFunds}
        setOwnFunds={setOwnFunds}
        navigate={navigate}
        user={user}
        onLogin={handleLogin}
        onAccount={handleAccount}
        showBlog={cfgBlogEnabled}
        onShareSite={cfgShareEnabled ? handleOpenShareSite : null}
      />
      {loginModalElement}
      {shareSiteModal}
      <Toaster position="top-right" />
    </>
  );
}
